import React, { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { PropertyCard } from "@/components/PropertyCard";
import { Carousel, CarouselContent, CarouselItem, CarouselPrevious, CarouselNext } from "@/components/ui/carousel";
import { Seo, ORG_JSONLD, WEBSITE_JSONLD } from "@/components/Seo";
import { useAuth } from "@/context/AuthContext";
import {
  Search, MapPin, Home as HomeIcon, Trees, Sprout, Store, ArrowRight, ShieldCheck,
  Eye, Users, Handshake, Phone, MessageCircle, Compass, FileCheck, TrendingUp, Building2,
} from "lucide-react";

const CATEGORIES = [
  { label: "Agricultural Land", sub: "Farm land & fertile plots", icon: Sprout, to: "/agricultural-land" },
  { label: "Premium Plots", sub: "HMDA / DTCP open plots", icon: Trees, to: "/plots" },
  { label: "Luxury Homes", sub: "Villas & apartments", icon: HomeIcon, to: "/homes" },
  { label: "Commercial", sub: "Offices & retail spaces", icon: Store, to: "/commercial" },
];

const WHY = [
  { icon: ShieldCheck, title: "Trusted Approach", body: "Every listing is checked by our team before it goes live." },
  { icon: Eye, title: "Transparent Deals", body: "Clear information and direct conversations — no hidden surprises." },
  { icon: Users, title: "Professional Team", body: "Guidance from enquiry to closing, at your pace." },
  { icon: Handshake, title: "Customer First", body: "We focus on the right property for your needs and budget." },
];

const STEPS = [
  { icon: Compass, title: "Explore", body: "Browse verified land, plots and homes on map or list." },
  { icon: MapPin, title: "Visit", body: "Shortlist and visit the properties that interest you." },
  { icon: FileCheck, title: "Verify", body: "Review documents and details before you decide." },
  { icon: TrendingUp, title: "Invest", body: "Complete the deal and build your future with confidence." },
];

export default function Home() {
  const navigate = useNavigate();
  const { siteConfig } = useAuth();
  const [q, setQ] = useState("");

  const whatsapp = siteConfig?.whatsapp || "919392108692";
  const phone = siteConfig?.phone || "9392108692";
  const waLink = `https://wa.me/${whatsapp}?text=${encodeURIComponent("Hi Dotlands Realty, I'm interested in a property.")}`;

  const { data: featured = [] } = useQuery({
    queryKey: ["featured"],
    queryFn: async () => (await api.get("/properties", { params: { featured: true } })).data,
  });

  const search = () => navigate(`/properties?q=${encodeURIComponent(q)}`);

  return (
    <div className="bg-[#FBF9F4]">
      <Seo
        title="Agricultural Land & Premium Plots for Sale in Telangana | Dotlands Realty"
        description="Explore verified agricultural land, HMDA & DTCP plots, residential and commercial properties near Hyderabad, Telangana. Find your ideal property with Dotlands Realty."
        path="/"
        jsonLd={[ORG_JSONLD, WEBSITE_JSONLD]}
      />

      {/* 2. Hero */}
      <section className="relative">
        <div className="absolute inset-0">
          <img
            src="https://images.unsplash.com/photo-1548941489-3e64750ebbaa?crop=entropy&cs=srgb&fm=jpg&q=85"
            alt="Aerial view of green agricultural farmland and open plots for sale in Telangana near Hyderabad"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-[#0A2A20] via-[#0A2A20]/85 to-[#0A2A20]/55" />
        </div>
        <div className="relative px-4 sm:px-6 lg:px-8 max-w-7xl mx-auto pt-20 pb-24 lg:pt-28 lg:pb-32">
          <div className="max-w-3xl">
            <span className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-[#C8A24C]/15 text-[#D8B75E] text-xs font-semibold tracking-wide uppercase border border-[#C8A24C]/40 mb-5">
              <ShieldCheck className="w-3.5 h-3.5" /> Verified land &amp; plot listings
            </span>
            <div className="w-16 h-[3px] bg-gradient-to-r from-[#C8A24C] to-transparent mb-5" />
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-heading font-extrabold text-white leading-[1.08] tracking-tight">
              Verified <span className="text-[#D8B75E]">Agricultural Land &amp; Premium Plots</span> for Sale in Telangana
            </h1>
            <p className="text-lg text-slate-200/90 mt-5 max-w-xl leading-relaxed">
              Discover verified agricultural land, HMDA/DTCP plots, premium homes and commercial properties near Hyderabad.
            </p>

            <div className="flex flex-wrap gap-3 mt-7">
              <Button data-testid="hero-explore-btn" onClick={() => navigate("/properties")} className="bg-[#C8A24C] hover:bg-[#A8863A] text-[#0E3B2E] font-bold h-12 px-6 rounded-full">
                Explore Properties <ArrowRight className="w-4 h-4 ml-1" />
              </Button>
              <Button data-testid="hero-post-btn" onClick={() => navigate("/list-property")} variant="outline" className="h-12 px-6 rounded-full border-white/40 bg-white/5 text-white hover:bg-white/15">
                Post Your Property
              </Button>
              <a href={waLink} target="_blank" rel="noreferrer" data-testid="hero-whatsapp-btn"
                className="inline-flex items-center gap-2 h-12 px-6 rounded-full bg-emerald-600 hover:bg-emerald-700 text-white font-semibold">
                <MessageCircle className="w-4 h-4" /> WhatsApp Us
              </a>
            </div>

            <div className="mt-8 bg-white rounded-2xl p-3 shadow-2xl flex flex-col sm:flex-row gap-3 max-w-2xl">
              <div className="flex items-center gap-2 flex-1 px-3">
                <Search className="w-5 h-5 text-slate-400" />
                <Input data-testid="hero-search-input" value={q} onChange={(e) => setQ(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && search()}
                  placeholder="Search by city, locality or project..."
                  className="border-0 focus-visible:ring-0 text-base px-0" />
              </div>
              <Button data-testid="hero-search-btn" onClick={search} className="bg-[#0E3B2E] hover:bg-[#0A2A20] text-white font-bold h-11 px-6 rounded-xl">
                Search
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* 3. Property Categories */}
      <section className="px-4 sm:px-6 lg:px-8 max-w-7xl mx-auto py-16">
        <div className="text-center max-w-2xl mx-auto">
          <div className="w-12 h-[3px] bg-[#C8A24C] mx-auto mb-4" />
          <h2 className="text-2xl sm:text-3xl font-heading font-extrabold text-[#0E3B2E]">Explore by category</h2>
          <p className="text-slate-500 mt-2">Find exactly what you're looking for across Telangana.</p>
        </div>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6 mt-10">
          {CATEGORIES.map((c) => (
            <Link key={c.to} to={c.to} data-testid={`cat-card-${c.to.replace("/", "")}`}
              className="group relative bg-white border border-slate-200 rounded-2xl p-6 hover:border-[#C8A24C] hover:shadow-xl hover:-translate-y-1 transition-all duration-300">
              <div className="w-12 h-12 rounded-xl bg-[#0E3B2E] flex items-center justify-center mb-4 group-hover:bg-[#C8A24C] transition-colors">
                <c.icon className="w-6 h-6 text-[#C8A24C] group-hover:text-[#0E3B2E] transition-colors" />
              </div>
              <h3 className="font-heading font-bold text-lg text-[#0E3B2E]">{c.label}</h3>
              <p className="text-sm text-slate-500 mt-1">{c.sub}</p>
              <span className="inline-flex items-center gap-1 text-sm font-semibold text-emerald-700 mt-4 opacity-0 group-hover:opacity-100 transition-opacity">
                View listings <ArrowRight className="w-4 h-4" />
              </span>
            </Link>
          ))}
        </div>
      </section>

      {/* 4. Featured Properties */}
      {featured.length > 0 && (
        <section className="px-4 sm:px-6 lg:px-8 max-w-7xl mx-auto pb-16" data-testid="featured-section">
          <Carousel opts={{ align: "start", loop: featured.length > 3 }} className="w-full">
            <div className="flex items-end justify-between mb-8 gap-4">
              <div>
                <div className="w-12 h-[3px] bg-[#C8A24C] mb-4" />
                <h2 className="text-2xl sm:text-3xl font-heading font-extrabold text-[#0E3B2E]">Featured properties</h2>
                <p className="text-slate-500 mt-2">Hand-picked verified listings across Telangana.</p>
              </div>
              <div className="flex items-center gap-2 shrink-0">
                <Link to="/properties" className="hidden sm:inline-flex items-center gap-1 text-sm font-semibold text-emerald-700 hover:text-emerald-800 mr-2">
                  View all <ArrowRight className="w-4 h-4" />
                </Link>
                <CarouselPrevious data-testid="featured-prev" className="static translate-y-0 left-auto right-auto w-10 h-10 border-[#0E3B2E]/20 text-[#0E3B2E] hover:bg-[#0E3B2E] hover:text-white" />
                <CarouselNext data-testid="featured-next" className="static translate-y-0 left-auto right-auto w-10 h-10 border-[#0E3B2E]/20 text-[#0E3B2E] hover:bg-[#0E3B2E] hover:text-white" />
              </div>
            </div>
            <CarouselContent className="-ml-6">
              {featured.map((p, i) => (
                <CarouselItem key={p.id} className="pl-6 basis-full sm:basis-1/2 lg:basis-1/3" data-testid={`featured-slide-${p.id}`}>
                  <PropertyCard p={p} index={i} />
                </CarouselItem>
              ))}
            </CarouselContent>
          </Carousel>
        </section>
      )}

      {/* 5. Founder / Brand Section */}
      <section className="bg-gradient-to-br from-[#0E3B2E] to-[#0A2A20] text-white py-16">
        <div className="px-4 sm:px-6 lg:px-8 max-w-7xl mx-auto grid lg:grid-cols-2 gap-10 items-center">
          <div>
            <div className="w-12 h-[3px] bg-[#C8A24C] mb-5" />
            <h2 className="text-3xl sm:text-4xl font-heading font-extrabold leading-tight">
              Building Dreams. <span className="text-[#D8B75E]">Creating Value.</span>
            </h2>
            <p className="text-slate-200/90 mt-5 leading-relaxed">
              Dotlands Realty is focused on connecting buyers and sellers with agricultural land, premium plots, homes and commercial properties across Telangana.
            </p>
            <p className="text-slate-200/80 mt-4 leading-relaxed">
              We believe buying land should be simple and transparent — so every listing is verified by our team, and our desk is always a message away.
            </p>
            <div className="flex flex-wrap gap-3 mt-7">
              <a href={waLink} target="_blank" rel="noreferrer"
                className="inline-flex items-center gap-2 bg-[#C8A24C] hover:bg-[#A8863A] text-[#0E3B2E] font-bold rounded-full px-6 h-12">
                <MessageCircle className="w-4 h-4" /> Talk to us
              </a>
              <Link to="/contact" className="inline-flex items-center gap-2 border border-white/30 hover:bg-white/10 rounded-full px-6 h-12 font-semibold">
                About Dotlands
              </Link>
            </div>
          </div>
          <div className="relative">
            <div className="absolute -inset-2 rounded-3xl bg-[#C8A24C]/20 blur-xl" aria-hidden="true" />
            <div className="relative rounded-3xl overflow-hidden border-2 border-[#C8A24C]/50 shadow-2xl bg-[#0A2A20]">
              <img
                src="/founder-banner.jpg"
                alt="Dotlands Realty — Raju R, Founder & Partner. Building Dreams. Creating Value."
                className="w-full h-auto object-contain"
                data-testid="founder-banner"
              />
            </div>
            <p className="text-center text-[#D8B75E] text-sm font-semibold mt-4 tracking-wide">Meet the Founder — Raju R, Founder &amp; Partner</p>
          </div>
        </div>
      </section>

      {/* 6. Why Choose Dotlands */}
      <section className="px-4 sm:px-6 lg:px-8 max-w-7xl mx-auto py-16">
        <div className="text-center max-w-2xl mx-auto">
          <div className="w-12 h-[3px] bg-[#C8A24C] mx-auto mb-4" />
          <h2 className="text-2xl sm:text-3xl font-heading font-extrabold text-[#0E3B2E]">Why choose Dotlands Realty</h2>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 mt-10">
          {WHY.map((w) => (
            <div key={w.title} className="bg-white border border-slate-200 rounded-2xl p-6 hover:shadow-lg transition-shadow">
              <div className="w-11 h-11 rounded-xl bg-[#C8A24C]/15 flex items-center justify-center mb-4">
                <w.icon className="w-5 h-5 text-[#A8863A]" />
              </div>
              <h3 className="font-heading font-bold text-[#0E3B2E]">{w.title}</h3>
              <p className="text-sm text-slate-500 mt-2 leading-relaxed">{w.body}</p>
            </div>
          ))}
        </div>
      </section>

      {/* 7. Agricultural Land Investment */}
      <section className="bg-white border-y border-slate-200">
        <div className="px-4 sm:px-6 lg:px-8 max-w-7xl mx-auto py-16 grid lg:grid-cols-2 gap-10 items-center">
          <div className="rounded-3xl overflow-hidden shadow-xl order-2 lg:order-1">
            <img
              src="https://images.unsplash.com/photo-1500382017468-9049fed747ef?crop=entropy&cs=srgb&fm=jpg&q=85"
              alt="Fertile agricultural farmland with open fields suitable for investment near Hyderabad, Telangana"
              className="w-full h-full object-cover aspect-[4/3]"
            />
          </div>
          <div className="order-1 lg:order-2">
            <div className="w-12 h-[3px] bg-[#C8A24C] mb-5" />
            <h2 className="text-3xl sm:text-4xl font-heading font-extrabold text-[#0E3B2E] leading-tight">
              Invest in Land. <span className="text-[#A8863A]">Build Your Future.</span>
            </h2>
            <p className="text-slate-600 mt-5 leading-relaxed">
              Land and plots around Hyderabad continue to attract buyers as infrastructure and connectivity expand. Choosing the right property, however, comes down to a few practical checks rather than the asking price alone.
            </p>
            <ul className="mt-5 space-y-3 text-slate-700">
              <li className="flex gap-3"><span className="text-[#C8A24C] font-bold">•</span> <span><strong>Location &amp; connectivity:</strong> distance to highways, ORR/RRR and growing towns.</span></li>
              <li className="flex gap-3"><span className="text-[#C8A24C] font-bold">•</span> <span><strong>Documentation:</strong> clear title, survey number and approvals such as HMDA or DTCP.</span></li>
              <li className="flex gap-3"><span className="text-[#C8A24C] font-bold">•</span> <span><strong>Suitability:</strong> soil, water access and how the land fits your purpose.</span></li>
            </ul>
            <div className="flex flex-wrap gap-3 mt-7">
              <Link to="/agricultural-land" className="inline-flex items-center gap-2 bg-[#0E3B2E] hover:bg-[#0A2A20] text-white font-semibold rounded-full px-6 h-12">
                Browse agricultural land <ArrowRight className="w-4 h-4" />
              </Link>
              <Link to="/guides" className="inline-flex items-center gap-2 border border-[#0E3B2E]/20 hover:bg-slate-50 text-[#0E3B2E] font-semibold rounded-full px-6 h-12">
                Read our guides
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* 8. How It Works */}
      <section className="bg-[#FBF9F4] border-y border-slate-200">
        <div className="px-4 sm:px-6 lg:px-8 max-w-7xl mx-auto py-16">
          <div className="text-center max-w-2xl mx-auto">
            <div className="w-12 h-[3px] bg-[#C8A24C] mx-auto mb-4" />
            <h2 className="text-2xl sm:text-3xl font-heading font-extrabold text-[#0E3B2E]">How it works</h2>
            <p className="text-slate-500 mt-2">A simple, transparent process from search to purchase.</p>
          </div>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-6 mt-10">
            {STEPS.map((s, i) => (
              <div key={s.title} className="relative bg-white border border-slate-200 rounded-2xl p-6">
                <span className="absolute top-4 right-5 text-4xl font-heading font-extrabold text-slate-100">{i + 1}</span>
                <div className="w-11 h-11 rounded-xl bg-[#0E3B2E] flex items-center justify-center mb-4">
                  <s.icon className="w-5 h-5 text-[#C8A24C]" />
                </div>
                <h3 className="font-heading font-bold text-[#0E3B2E]">{s.title}</h3>
                <p className="text-sm text-slate-500 mt-2 leading-relaxed">{s.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* 10. Customer Enquiry CTA */}
      <section className="px-4 sm:px-6 lg:px-8 max-w-7xl mx-auto py-16">
        <div className="relative overflow-hidden rounded-3xl bg-gradient-to-br from-[#0E3B2E] to-[#0A2A20] px-6 sm:px-12 py-14 text-center">
          <div className="absolute -top-10 -right-10 w-48 h-48 rounded-full bg-[#C8A24C]/10 blur-2xl" aria-hidden="true" />
          <div className="relative">
            <div className="w-14 h-[3px] bg-[#C8A24C] mx-auto mb-6" />
            <h2 className="text-3xl sm:text-4xl font-heading font-extrabold text-white">Looking for the Right Property?</h2>
            <p className="text-slate-200/80 mt-4 max-w-xl mx-auto">Talk to the Dotlands Realty desk and we'll help you find land, plots or a home that fits your needs.</p>
            <div className="flex flex-col sm:flex-row justify-center gap-3 mt-8">
              <a href={waLink} target="_blank" rel="noreferrer" data-testid="cta-whatsapp-btn"
                className="inline-flex items-center justify-center gap-2 bg-emerald-600 hover:bg-emerald-700 text-white font-bold rounded-full px-8 h-14 text-lg">
                <MessageCircle className="w-5 h-5" /> WhatsApp Us
              </a>
              <a href={`tel:+91${phone}`} data-testid="cta-call-btn"
                className="inline-flex items-center justify-center gap-2 bg-[#C8A24C] hover:bg-[#A8863A] text-[#0E3B2E] font-bold rounded-full px-8 h-14 text-lg">
                <Phone className="w-5 h-5" /> Call Dotlands Realty
              </a>
            </div>
            <p className="text-slate-300/70 text-sm mt-5">+91 {phone}</p>
          </div>
        </div>
      </section>
    </div>
  );
}
